import { useEffect, useMemo, useState } from 'react';
import { MessageCircle, Mail, Users, Megaphone, Contact, SquarePen, Phone, Search, Hash, BellOff } from 'lucide-react';
import ListToolbar, { PohledPill } from '../layout/ListToolbar';
import Avatar from '../../lib/avatar';
import { SECTION_COLOR, SECTION_BG, pocetLidi, formatCasListu, druhyUcastnik, casSmeny } from './komunikace';

/**
 * Komunikace → seznam konverzací. Norma §2: hlavička `ListToolbar`, pohledy
 * s počty. Na mobilu a v přehledu přes celou šířku jako pilulky; v úzkém
 * sloupci vedle chatu jako ovladač — pět pilulek se tam lámalo do dvou řad.
 *
 * Hledání hledá i **v obsahu zpráv** (od tří znaků, `/api/chat/hledat`) —
 * dřív šlo najít jen konverzaci podle jména.
 *
 * „Lidé" je adresář kolegů: jiná sada než konverzace, ale tentýž přepínač
 * toho, co seznam ukazuje — kolegu člověk shání právě tady.
 */
export const POHLEDY = [
    { id: 'vse', label: 'Vše', Icon: MessageCircle, title: 'Všechny konverzace' },
    { id: 'neprecte', label: 'Nepřečtené', Icon: Mail, upozorneni: true, title: 'Konverzace s nepřečtenou zprávou', patri: k => k.neprectenych > 0 },
    { id: 'kanaly', label: 'Kanály', Icon: Hash, title: 'Kanály s automatickým členstvím — pozice, dnešní směna, všichni', patri: k => k.typ === 'kanal' },
    { id: 'skupiny', label: 'Skupiny', Icon: Users, title: 'Konverzace víc lidí — směna, provoz, projekt', patri: k => k.typ === 'skupina' },
    { id: 'sdeleni', label: 'Sdělení', Icon: Megaphone, title: 'Hlášení shora — odpovídat se v nich nedá', patri: k => k.typ === 'sdeleni' },
    { id: 'lide', label: 'Lidé', Icon: Contact, adresar: true, title: 'Adresář kolegů — napsat i zavolat' },
];

const PRAZDNE = {
    vse: 'Zatím žádné konverzace',
    neprecte: 'Nic nepřečteného',
    kanaly: 'Žádný kanál',
    skupiny: 'Žádná skupinová konverzace',
    sdeleni: 'Žádná sdělení',
};

const TYP_POPIS = { soukromy: 'Soukromá', skupina: 'Skupina', sdeleni: 'Sdělení', kanal: 'Kanál' };

export default function SeznamKonverzaci({
    user, isDark, isMobile, siroky, mutedColor, inputStyle, cardStyle,
    konverzace, onlineIds, activeId, onVyber,
    pohled, setPohled, hledani, setHledani,
    lide, onKarta, onNapsat, onNova, rezervaDole = null, nahore = null,
}) {
    const kompaktni = isMobile;
    const aktivniPohled = POHLEDY.find(p => p.id === pohled) || POHLEDY[0];
    const cara = isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)';

    // Počty ze všech konverzací, ne z prošlých hledáním — jinak by psaní
    // do pole přepsalo všechna čísla naráz.
    const pocty = useMemo(() => {
        const o = { vse: konverzace.length, lide: lide?.length ?? null };
        for (const p of POHLEDY) if (p.patri) o[p.id] = konverzace.filter(p.patri).length;
        return o;
    }, [konverzace, lide]);

    const hledat = hledani.trim().toLowerCase();
    const nazevKonverzace = (k) => k.nazev || druhyUcastnik(k, user?.id)?.jmeno || 'Soukromý chat';

    const filtrovane = aktivniPohled.adresar ? [] : konverzace.filter(k => {
        if (aktivniPohled.patri && !aktivniPohled.patri(k)) return false;
        if (hledat && !nazevKonverzace(k).toLowerCase().includes(hledat)) return false;
        return true;
    });

    // ── Hledání v obsahu zpráv ──
    const [nalezeneZpravy, setNalezeneZpravy] = useState([]);
    useEffect(() => {
        if (aktivniPohled.adresar || hledat.length < 3) { setNalezeneZpravy([]); return; }
        const t = setTimeout(() => {
            fetch(`/api/chat/hledat?q=${encodeURIComponent(hledat)}`)
                .then(r => (r.ok ? r.json() : []))
                .then(d => setNalezeneZpravy(Array.isArray(d) ? d : []))
                .catch(() => setNalezeneZpravy([]));
        }, 300);
        return () => clearTimeout(t);
    }, [hledat, aktivniPohled.adresar]);

    // ── Adresář ──
    const lideFiltr = (lide || []).filter(c => !hledat || (c.jmeno + ' ' + (c.pozice || '') + ' ' + (c.telefon || '')).toLowerCase().includes(hledat));
    const lideNaSmene = lideFiltr.filter(c => c.smena);
    const lideOstatni = lideFiltr.filter(c => !c.smena);

    // ── Kousky řádků ──
    const avatarKonverzace = (k, size) => {
        const druhy = druhyUcastnik(k, user?.id);
        const online = k.typ === 'soukromy'
            ? (!!druhy && onlineIds.has(druhy.id))
            : !!k.ucastnici?.some(u => u.id !== user?.id && onlineIds.has(u.id));
        const url = k.typ === 'soukromy' ? druhy?.avatar_url : k.avatar_url;
        const emoji = k.typ === 'soukromy' ? null : (k.ikona || (k.typ === 'sdeleni' ? '📢' : k.typ === 'kanal' ? '#' : null));
        if (!url && emoji) {
            return (
                <div className="relative shrink-0 rounded-full flex items-center justify-center"
                    style={{ width: size, height: size, background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)', fontSize: Math.round(size * 0.46) }}>
                    {emoji}
                </div>
            );
        }
        return <Avatar url={url} jmeno={k.typ === 'soukromy' ? druhy?.jmeno : k.nazev} size={size} online={online} isDark={isDark} />;
    };

    const podtitul = (k) => {
        if (!k.posledni_zprava) {
            return k.typ === 'skupina' && k.pocet_ucastniku ? `${pocetLidi(k.pocet_ucastniku)} · zatím nikdo nepsal` : 'Žádné zprávy';
        }
        const z = k.posledni_zprava;
        const obsah = z.obsah
            || (z.priloha_mime?.startsWith('image/') ? '📷 Fotka'
                : z.priloha_mime?.startsWith('video/') ? '🎬 Video'
                : z.priloha_mime ? '📎 Soubor' : '…');
        return `${z.odesilatel_id === user?.id ? 'Vy' : z.autor}: ${obsah}`;
    };

    const Odznak = ({ n }) => (n > 0 ? (
        <span className="shrink-0 text-[11px] font-bold rounded-full tabular-nums"
            style={{ background: SECTION_COLOR, color: '#fff', minWidth: 20, padding: '1px 6px', textAlign: 'center' }}>
            {n > 99 ? '99+' : n}
        </span>
    ) : null);

    const radekKonverzace = (k) => {
        const aktivni = activeId === k.id;
        const tucne = k.neprectenych > 0;
        return (
            <button key={k.id} type="button" onClick={() => onVyber(k.id)}
                className="w-full flex items-center text-left"
                style={{
                    gap: 12, padding: kompaktni ? '10px 14px' : '9px 14px', minHeight: kompaktni ? 68 : 60,
                    background: aktivni ? SECTION_BG : 'transparent',
                    boxShadow: aktivni ? `inset 3px 0 0 ${SECTION_COLOR}` : 'none',
                    border: 'none', borderBottom: `1px solid ${cara}`, cursor: 'pointer',
                }}>
                {avatarKonverzace(k, kompaktni ? 46 : 38)}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                        <span className="truncate flex items-center gap-1.5" style={{ color: 'var(--text-primary)', fontSize: kompaktni ? 15 : 13.5, fontWeight: tucne ? 700 : 600 }}>
                            <span className="truncate">{nazevKonverzace(k)}</span>
                            {k.ztlumeno && <BellOff size={12} style={{ color: mutedColor, flexShrink: 0 }} aria-label="Ztlumeno" />}
                        </span>
                        {!siroky && k.posledni_zprava && (
                            <span className="shrink-0 text-[11px]" style={{ color: mutedColor }}>{formatCasListu(k.posledni_zprava.cas)}</span>
                        )}
                    </div>
                    <div className="flex items-center justify-between gap-2" style={{ marginTop: 2 }}>
                        <span className="truncate" style={{ fontSize: kompaktni ? 13 : 12, color: tucne ? 'var(--text-secondary)' : mutedColor }}>{podtitul(k)}</span>
                        {!siroky && <Odznak n={k.neprectenych} />}
                    </div>
                </div>
                {siroky && (
                    <>
                        <span className="shrink-0 text-xs" style={{ width: 90, color: mutedColor }}>{TYP_POPIS[k.typ] || ''}</span>
                        <span className="shrink-0 text-xs tabular-nums" style={{ width: 70, color: mutedColor }}>{k.typ === 'soukromy' ? '' : pocetLidi(k.pocet_ucastniku || 0)}</span>
                        <span className="shrink-0 text-xs tabular-nums text-right" style={{ width: 64, color: mutedColor }}>{formatCasListu(k.posledni_zprava?.cas)}</span>
                        <span className="shrink-0 flex justify-end" style={{ width: 40 }}><Odznak n={k.neprectenych} /></span>
                    </>
                )}
            </button>
        );
    };

    const nadpis = (text) => (
        <div key={'n-' + text} className="text-[10.5px] font-bold uppercase"
            style={{ color: mutedColor, letterSpacing: '0.06em', padding: '14px 14px 4px' }}>{text}</div>
    );

    const ikonovka = { width: kompaktni ? 40 : 32, height: kompaktni ? 40 : 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'var(--border-default)'}`, background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' };

    const radekCloveka = (c) => {
        const tel = (c.telefon || '').replace(/\s+/g, '');
        const pod = [c.pozice, c.smena ? `${casSmeny(c.smena.cas_od)}–${casSmeny(c.smena.cas_do)}` : null].filter(Boolean).join(' · ');
        return (
            <div key={c.id} className="flex items-center" style={{ gap: 12, padding: '8px 14px', minHeight: kompaktni ? 64 : 56, borderBottom: `1px solid ${cara}` }}>
                <button type="button" onClick={() => onKarta(c)} className="flex items-center flex-1 min-w-0 text-left"
                    style={{ gap: 12, background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}>
                    <Avatar url={c.avatar_url} jmeno={c.jmeno} size={kompaktni ? 44 : 36} online={onlineIds.has(c.id)} isDark={isDark} />
                    <div className="min-w-0">
                        <p className="truncate font-semibold" style={{ color: 'var(--text-primary)', fontSize: kompaktni ? 15 : 13.5, margin: 0 }}>{c.jmeno}</p>
                        <p className="truncate" style={{ color: mutedColor, fontSize: kompaktni ? 13 : 12, margin: 0 }}>{pod || 'Kolega'}</p>
                    </div>
                </button>
                {tel && <a href={'tel:' + tel} aria-label={'Volat ' + c.jmeno} style={ikonovka}><Phone size={16} /></a>}
                <button type="button" onClick={() => onNapsat(c)} aria-label={'Napsat ' + c.jmeno} style={{ ...ikonovka, color: SECTION_COLOR }}>
                    <MessageCircle size={16} />
                </button>
            </div>
        );
    };

    const radekNalezu = (n) => {
        const k = konverzace.find(x => x.id === n.konverzace_id);
        return (
            <button key={'z' + n.zprava_id} type="button" onClick={() => onVyber(n.konverzace_id)}
                className="w-full text-left flex" style={{ gap: 10, padding: '9px 14px', background: 'transparent', border: 'none', borderBottom: `1px solid ${cara}`, cursor: 'pointer' }}>
                <Search size={14} style={{ color: mutedColor, marginTop: 3, flexShrink: 0 }} />
                <div className="min-w-0 flex-1">
                    <div className="flex justify-between gap-2">
                        <span className="truncate text-[12.5px] font-semibold" style={{ color: 'var(--text-primary)' }}>
                            {n.konverzace || (k ? nazevKonverzace(k) : 'Soukromý chat')}
                        </span>
                        <span className="shrink-0 text-[11px]" style={{ color: mutedColor }}>{formatCasListu(n.cas)}</span>
                    </div>
                    <p className="text-xs" style={{ color: 'var(--text-secondary)', margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        <span style={{ color: mutedColor }}>{n.vlastni ? 'Vy' : n.autor}: </span>{n.obsah}
                    </p>
                </div>
            </button>
        );
    };

    const prazdno = (text) => <div className="text-center text-sm" style={{ color: mutedColor, padding: '32px 16px' }}>{text}</div>;

    const pouzitOvladac = !siroky && !kompaktni;

    return (
        <div className={kompaktni ? 'flex flex-col flex-1 min-h-0' : 'rounded-xl overflow-hidden flex flex-col flex-1 min-h-0'}
            style={kompaktni ? null : cardStyle}>
            <ListToolbar
                siroky={siroky} kompaktni={kompaktni} sklo isDark={isDark} mutedColor={mutedColor} inputStyle={inputStyle} barva={SECTION_COLOR}
                hledani={{
                    value: hledani, onChange: setHledani,
                    placeholder: aktivniPohled.adresar ? 'Hledat kolegu, pozici, telefon…' : 'Hledat konverzaci i ve zprávách…',
                    placeholderKratky: 'Hledat',
                }}
                akce={[{ id: 'nova', label: 'Nová konverzace', Icon: SquarePen, primarni: true, onClick: onNova }]}
                pohledy={pouzitOvladac ? POHLEDY.map(p => ({
                    id: p.id, label: p.label, Icon: p.Icon, pocet: pocty[p.id], title: p.title,
                    upozorneni: p.upozorneni, barva: p.upozorneni ? SECTION_COLOR : undefined,
                })) : null}
                pohledAktivni={aktivniPohled.id}
                onPohled={setPohled}
                pohled={pouzitOvladac ? null : POHLEDY.map(p => (
                    <PohledPill key={p.id} Icon={p.Icon} label={p.label} pocet={pocty[p.id]}
                        aktivni={aktivniPohled.id === p.id} upozorneni={p.upozorneni && pocty[p.id] > 0}
                        onClick={() => setPohled(p.id)} title={p.title}
                        isDark={isDark} mutedColor={mutedColor} barva={SECTION_COLOR} kompaktni={kompaktni} />
                ))}
            />

            <div className="flex-1" style={{ overflowY: 'auto', minHeight: 0 }}>
                {!aktivniPohled.adresar && !hledat && nahore}
                {aktivniPohled.adresar ? (
                    lide === null ? prazdno('Načítám…')
                        : lideFiltr.length === 0 ? prazdno('Nikdo takový tu není')
                            : (
                                <>
                                    {lideNaSmene.length > 0 && nadpis('Dnes na směně')}
                                    {lideNaSmene.map(radekCloveka)}
                                    {lideOstatni.length > 0 && lideNaSmene.length > 0 && nadpis('Ostatní')}
                                    {lideOstatni.map(radekCloveka)}
                                </>
                            )
                ) : (
                    <>
                        {filtrovane.length === 0 && nalezeneZpravy.length === 0 && prazdno(hledat ? 'Nic se neshoduje' : (PRAZDNE[pohled] || 'Žádné konverzace'))}
                        {filtrovane.map(radekKonverzace)}
                        {nalezeneZpravy.length > 0 && nadpis(`Ve zprávách · ${nalezeneZpravy.length}`)}
                        {nalezeneZpravy.map(radekNalezu)}
                    </>
                )}
                {rezervaDole && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>
        </div>
    );
}

